import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import axios from "axios";
import {
  replaceTableData,
  clearTableData,
} from "views/Sales-Sections/salesReturn/slice/tableDataSlice";
import { updateSelected } from "../slice/returnHeaderSlice";
import {
  setDiscPer,
  setDiscAmt,
  setTaxAmt,
  setTotal,
  setNetTotal,
  setToDefaultCalc,
  setTaxPer,
  setOther,
} from "../slice/discAndTaxSlice";
import { baseUrl, redirectUrl } from "config";
import dayjs from "dayjs";
import { useSnackBar } from "context/snackbarContext";
import { setModuleHeading } from "commonSlice/commonSlice";
import getConditionStatus from "utils/getPrevStatusFunc";
import { useNavigate } from "react-router";

//--------// APIS_USED_FOR_THIS_COMPONENT //---------//
const CUSTOMER_OUTLET_DEALER = "/getMasterAndSalesItem";
const CUSTOMER_OUTLET_DEALER_B2C = "/getMasterAndSalesItemB2C";
const GET_RETURN_NO = "/getMaxBillSr";
const UPDATE_BILL = "/getSrToUpdate";
const GET_PARTY_BALANCE = "/getPartynameCurrentBalance";
const GET_B2B_BILL = "/getBillToUpdate";
const GET_B2C_BILL = "/getBillToUpdate8BNEW";

function SalesHeader() {
  const moduleName = "SALES RETURN";
  const { returnHeader, common: commonState } = useSelector((state) => ({
    returnHeader: state.returnHeader,
    common: state.common,
  }));

  const dispatch = useDispatch();
  const [headerInfo, setHeaderInfo] = useState([]);
  const { showSnackbar } = useSnackBar();
  const { taxUtilitiesListData, decimalCount, userDetail } = commonState;
  const tableData = useSelector((state) => state.tableData);
  const navigate = useNavigate();

  const [priceLevelPopup, setPriceLevelPopup] = useState(false);
  const [pendingPriceLevel, setPendingPriceLevel] = useState(null);

  // Memoized callback for fetching customer balance
  const onHandleCustomerBalance = useCallback(
    async (name) => {
      try {
        const res = await axios.post(
          baseUrl + GET_PARTY_BALANCE,
          {
            date: dayjs(),
            name,
          },
          { withCredentials: true },
        );
        dispatch(
          updateSelected({
            balance: Number(
              res.data[0][0].balance.truncateDecimal(decimalCount),
            ),
            vatNo: res.data[1][0].TinNo,
            address: `${res.data[1][0].BuildingNo},${res.data[1][0].streetName},${res.data[1][0].pin_code},${res.data[1][0].district}`,
          }),
        );
      } catch (err) {
        if (err.response?.status === 403 || err.response?.status === 401) {
          redirectUrl();
        } else {
          // Log or handle other errors more specifically

          showSnackbar("error", "Failed to fetch customer balance.");
        }
      }
    },
    [decimalCount, dispatch, showSnackbar], // Add showSnackbar to dependencies
  );

  const invTypMenuItems = [
    { id: 1, label: "B2B" },
    { id: 1, label: "B2C" },
  ];

  // Memoized callback for date change
  const handleChangeDate = useCallback(
    (selectedDate) => {
      dispatch(updateSelected({ date: selectedDate }));
    },
    [dispatch],
  );

  // Memoized callback for fetching master data (customer, outlet, dealer)
  const fetchData = useCallback(
    async (apiNam) => {
      try {
        const res = await axios.get(baseUrl + apiNam, {
          withCredentials: true,
        });
        setHeaderInfo(res?.data);
        console.log(res.data, "103");
        dispatch(
          updateSelected({
            customer: res?.data?.[0]?.[0],
            contact: res?.data?.[0]?.[0]?.Ph,
            dealer: res?.data?.[1]?.[0],
            outlet: res?.data?.[2]?.[0],
          }),
        );
      } catch (error) {
        if (error.response?.status === 403 || error.response?.status === 401) {
          redirectUrl();
        } else {
          console.error("Error fetching header info:", error);
          showSnackbar("error", "Failed to fetch header information.");
        }
      }
    },
    [dispatch, showSnackbar], // Add showSnackbar to dependencies
  );

  // Memoized callback for getting return number
  const getMasterData = useCallback(async () => {
    try {
      const res = await axios.get(baseUrl + GET_RETURN_NO, {
        withCredentials: true,
      });
      dispatch(
        updateSelected({
          retNo: res.data[0].BillNo,
          retBill: res.data[0].BillNo,
        }),
      );
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        redirectUrl();
      } else {
        console.error("Error fetching return number:", error);
        showSnackbar("error", "Failed to fetch return number.");
      }
    }
  }, [dispatch, showSnackbar]); // Add showSnackbar to dependencies

  // Effect for setting tax enabled status
  useEffect(() => {
    const taxEnabled = taxUtilitiesListData?.find(
      (data) => data.Typ === "SALES_B2B_AND_B2C",
    );

    if (taxEnabled) {
      dispatch(updateSelected({ taxEnabled: taxEnabled.Nam === "YES" }));
    }
  }, [taxUtilitiesListData, dispatch]);

  // Initial data fetching and module heading setup
  useEffect(() => {
    fetchData(CUSTOMER_OUTLET_DEALER);
    getMasterData();
    dispatch(updateSelected({ invtyp: invTypMenuItems[0] }));
    dispatch(setModuleHeading(moduleName));

    if (!getConditionStatus(userDetail, "Sales Return", "view")) {
      navigate("/");
    }
    // `fetchData` and `getMasterData` are stable due to `useCallback`
  }, [dispatch, userDetail, navigate]);

  // Memoized callback for handling return edit (fetching existing return data)
  const handleEditReturn = useCallback(
    async (no) => {
      if (no === undefined || no === null) return; // Prevent API call with invalid 'no'

      try {
        const res = await axios.post(
          baseUrl + UPDATE_BILL,
          { BillNo: no },
          { withCredentials: true },
        );

        if (res.data[1].length > 0) {
          const data = res.data[0][0];
          console.log(data.narration, "ggg");
          dispatch(
            updateSelected({
              customer: { Name: data.BillPartyName },
              date: dayjs(data.BillDate),
              dealer: { Name: data?.Employee },
              outlet: { Typ: data?.godown },
              invtyp: { label: data?.bill_form },
              invo: data?.billno,
              note: data?.narration,
              // Consolidate other calculations into one dispatch if possible, or keep separate for clarity
            }),
          );
          dispatch(replaceTableData(res.data[1]));
          dispatch(setDiscPer(data.BillDiscPercField));
          dispatch(setDiscAmt(data.BillDiscAmtField));
          dispatch(setTaxAmt(data.TaxAmt));
          dispatch(setTaxPer(data.BillTaxField));
          dispatch(setTotal(data.BillTotalField));
          dispatch(setNetTotal(data.BillNetTotalField));
          dispatch(setOther(data.BillPackageField));
        } else {
          // If no data, clear everything
          dispatch(setToDefaultCalc());
          dispatch(clearTableData());
          showSnackbar("info", "No return data found for this number.");
        }
      } catch (error) {
        if (error.response?.status === 403 || error.response?.status === 401) {
          redirectUrl();
        } else {
          console.error("Error fetching return details:", error);
          showSnackbar("error", "Failed to fetch return details.");
        }
      }
    },
    [dispatch, showSnackbar],
  );

  // Memoized callback for incrementing return number
  const handleIncrement = useCallback(() => {
    const newRetNo = returnHeader.retNo + 1;
    if (newRetNo <= returnHeader.retBill) {
      dispatch(
        updateSelected({
          retNo: newRetNo,
          edit: newRetNo < returnHeader.retBill,
        }),
      );
      handleEditReturn(newRetNo);
    }
  }, [dispatch, handleEditReturn, returnHeader.retNo, returnHeader.retBill]);

  // Memoized callback for decrementing return number
  const handleDecrement = useCallback(() => {
    const newRetNo = returnHeader.retNo - 1;
    if (newRetNo >= 0) {
      // Ensure return number doesn't go below 0
      dispatch(
        updateSelected({
          retNo: newRetNo,
          edit: newRetNo < returnHeader.retBill,
        }),
      );
      handleEditReturn(newRetNo);
    }
  }, [dispatch, handleEditReturn, returnHeader.retNo, returnHeader.retBill]);

  // Memoized callback for customer selection
  const handleCustomer = useCallback(
    (e, newValue) => {
      if (newValue) {
        dispatch(updateSelected({ customer: newValue, contact: newValue.Ph }));
        onHandleCustomerBalance(newValue.Name);
      } else {
        // If customer is cleared, reset related fields
        dispatch(
          updateSelected({
            customer: null,
            contact: "",
            balance: 0,
            vatNo: "",
            address: "",
          }),
        );
      }
      setTimeout(() => {
        const barcodeInput = document.getElementById("barcode-input");
        if (barcodeInput) {
          barcodeInput.focus();
        }
      }, 100);
    },
    [dispatch, onHandleCustomerBalance],
  );

  // Memoized callback for return number text field change
  const handleRetNo = useCallback(
    (e) => {
      const value = parseInt(e.target.value);
      if (!isNaN(value)) {
        dispatch(
          updateSelected({ retNo: value, edit: value < returnHeader.retBill }),
        );
        handleEditReturn(value);
      } else {
        // If input is cleared or invalid, set retNo to 0 or appropriate default
        dispatch(updateSelected({ retNo: 0, edit: false }));
        dispatch(setToDefaultCalc());
        dispatch(clearTableData());
      }
    },
    [dispatch, handleEditReturn, returnHeader.retBill],
  );

  // Memoized callback for Autocomplete changes (Outlet, Dealer, InvTyp)
  const handleAutoCompleteChange = useCallback(
    (e, newValue, name) => {
      dispatch(updateSelected({ [name]: newValue }));

      if (name === "invtyp" && newValue) {
        if (newValue.label === "B2C") {
          fetchData(CUSTOMER_OUTLET_DEALER_B2C);
        } else {
          fetchData(CUSTOMER_OUTLET_DEALER);
        }
      }
    },
    [dispatch, fetchData],
  );

  // State to hold the debounce timeout ID
  const [invoiceNoTimeout, setInvoiceNoTimeout] = useState(null);

  // Memoized callback for invoice number change with debouncing
  const handleInvNo = useCallback(
    async (e) => {
      const invoiceNo = e.target.value;
      dispatch(updateSelected({ invo: invoiceNo }));

      // Clear previous timeout to debounce
      if (invoiceNoTimeout) {
        clearTimeout(invoiceNoTimeout);
      }

      if (!invoiceNo) {
        dispatch(setToDefaultCalc());
        dispatch(clearTableData());
        showSnackbar("info", "Invoice number cleared.");
        return;
      }

      // Set a new timeout
      const newTimeoutId = setTimeout(async () => {
        const selectedUrl =
          returnHeader.invtyp?.label === "B2B" ? GET_B2B_BILL : GET_B2C_BILL;
        try {
          const res = await axios.post(
            `${baseUrl}${selectedUrl}`,
            { BillNo: invoiceNo },
            { withCredentials: true },
          );

          if (res.data[1].length > 0) {
            const data = res.data[0][0];
            console.log(data, "data-318");
            dispatch(
              updateSelected({
                customer: { Name: data.BillPartyName },
                date: dayjs(data.BillDate),
                dealer: { Name: data.Lorry }, // Note: Data from API might be inconsistent (Employee vs Lorry)
                outlet: { Typ: data.godown },
                invo: data?.billno,
              }),
            );
            dispatch(replaceTableData(res.data[1]));
            dispatch(setDiscPer(data.BillDiscPercField));
            dispatch(setDiscAmt(data.BillDiscAmtField));
            dispatch(setTaxAmt(data.TaxAmt));
            dispatch(setTaxPer(data.BillTaxField));
            dispatch(setTotal(data.BillTotalField));
            dispatch(setNetTotal(data.BillNetTotalField));
            dispatch(setOther(data.BillPackageField));
          } else {
            showSnackbar("error", "No data found for this Invoice Number.");
            dispatch(setToDefaultCalc());
            dispatch(clearTableData());
          }
        } catch (error) {
          if (
            error.response?.status === 403 ||
            error.response?.status === 401
          ) {
            redirectUrl();
          } else {
            console.error("Error fetching invoice details:", error);
            showSnackbar("error", "Failed to fetch invoice details.");
          }
        }
      }, 500); // Debounce for 500ms
      setInvoiceNoTimeout(newTimeoutId);
    },
    [dispatch, returnHeader.invtyp?.label, showSnackbar, invoiceNoTimeout], // Include invoiceNoTimeout in dependencies
  );

  const priceLevel = [
    { id: 1, label: "A" },
    { id: 2, label: "B" },
    { id: 3, label: "C" },
  ];

  // Memoized callback for generic Autocomplete (Price level)
  const handleAuto = useCallback(
    (name, newValue) => {
      if (name === "priceLevel" && tableData.length > 0) {
        setPendingPriceLevel(newValue);
        setPriceLevelPopup(true);
      } else {
        dispatch(updateSelected({ [name]: newValue }));
      }
    },
    [dispatch, tableData],
  );

  const confirmPriceLevelUpdate = async () => {
    try {
      const TaxEnabled = taxUtilitiesListData?.find(
        (data) => data.Typ === "SALES_B2B_AND_B2C",
      );

      const response = await axios.post(
        baseUrl + "/getBulkPriceLevelRates",
        {
          priceLevel: pendingPriceLevel?.label,
          table: tableData,
        },
        { withCredentials: true },
      );

      if (response.data && response.data.tablDat) {
        const updatedTable = response.data.tablDat.map((item) => {
          let calculatedRate = parseFloat(item.Rate1) || 0;
          let calculatedQty = parseFloat(item.qty) || 0;
          let taxAmt = 0;
          let total = 0;

          if (TaxEnabled?.Nam === "YES") {
            taxAmt =
              (calculatedQty * calculatedRate * item.Tax1) / (100 + item.Tax1);
            total = calculatedQty * calculatedRate;
          } else {
            taxAmt = (calculatedQty * calculatedRate * item.Tax1) / 100;
            total = calculatedQty * calculatedRate + taxAmt;
          }

          return {
            ...item,
            Rate1: calculatedRate,
            taxAmt: taxAmt,
            total: total,
          };
        });

        dispatch(replaceTableData(updatedTable));
      }

      if (pendingPriceLevel !== null) {
        dispatch(updateSelected({ priceLevel: pendingPriceLevel }));
      }
    } catch (error) {
      console.error("Failed to update price level rates", error);
    } finally {
      setPriceLevelPopup(false);
      setPendingPriceLevel(null);
    }
  };

  const cancelPriceLevelUpdate = () => {
    setPriceLevelPopup(false);
    setPendingPriceLevel(null);
  };

  const handleChange = (e) => {
    const { value } = e.target;
    dispatch(updateSelected({ note: value }));
  };

  return (
    <Box sx={{ px: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={5} md={3}>
              <Autocomplete
                disablePortal
                id="outlet-autocomplete"
                options={headerInfo[2] || []}
                getOptionLabel={(option) => option.Typ || ""}
                fullWidth
                onChange={(e, newValue) =>
                  handleAutoCompleteChange(e, newValue, "outlet")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Branch" size="small" />
                )}
                value={returnHeader.outlet}
              />
            </Grid>
            <Grid item xs={6} sm={5} md={3}>
              <TextField
                fullWidth
                type="number" onWheel={(e) => e.target.blur()}
                label="Return No"
                size="small"
                value={returnHeader.retNo}
                onChange={handleRetNo}
                inputProps={{
                  min: 0,
                  max: returnHeader.retBill,
                  style: {
                    fontWeight: "bold",
                  },
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                InputProps={{
                  startAdornment:
                    returnHeader.retNo > 1 ? (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={handleDecrement}
                          disabled={returnHeader.retNo === 0}
                        >
                          <RemoveCircleIcon color="primary" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  endAdornment:
                    returnHeader.retNo < returnHeader.retBill ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleIncrement}
                          disabled={returnHeader.retNo === returnHeader.retBill}
                        >
                          <AddCircleIcon color="primary" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    paddingLeft: "0.2px",
                    paddingRight: "0.2px",
                  },
                  input: { textAlign: "center" },
                }}
              />
            </Grid>
            <Grid item xs={6} sm={5} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  sx={{ width: "100%" }}
                  label="Date"
                  className="datepick"
                  value={returnHeader.date}
                  onChange={handleChangeDate}
                  format="DD/MM/YYYY"
                  renderInput={(props) => (
                    <TextField {...props} fullWidth size="small" />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={5} md={3}>
              <Autocomplete
                disablePortal
                id="price-level-autocomplete"
                options={priceLevel}
                getOptionLabel={(option) => option.label || ""}
                fullWidth
                onChange={(e, v) => handleAuto("priceLevel", v)}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Price level" />
                )}
                value={returnHeader.priceLevel}
              />
            </Grid>
            <Grid item xs={6} sm={5} md={3}>
              <Autocomplete
                disablePortal
                id="invtyp-autocomplete"
                options={invTypMenuItems}
                getOptionLabel={(option) => option.label || ""}
                fullWidth
                onChange={(e, newValue) =>
                  handleAutoCompleteChange(e, newValue, "invtyp")
                }
                renderInput={(params) => (
                  <TextField {...params} label="InvTyp" size="small" />
                )}
                value={returnHeader.invtyp}
              />
            </Grid>
            <Grid item xs={6} sm={5} md={3}>
              <TextField
                type="number" onWheel={(e) => e.target.blur()}
                label="Invoice No"
                onChange={handleInvNo}
                size="small"
                value={returnHeader.invo || ""}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Autocomplete
                disableClearable
                id="customer-autocomplete"
                options={headerInfo[0] || []}
                getOptionLabel={(option) => {
                  if (String(option.Name) === "3") return "CASH";
                  if (String(option.Name) === "4") return "CASH PURCHASE";
                  return option.Name || "";
                }}
                onChange={handleCustomer}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Party Name"
                    size="small"
                    autoFocus
                  />
                )}
                value={returnHeader.customer}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setTimeout(() => {
                      const barcodeInput =
                        document.getElementById("barcode-input");
                      if (barcodeInput) {
                        barcodeInput.focus();
                      }
                    }, 100);
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={5} md={4}>
              <Autocomplete
                disablePortal
                id="dealer-autocomplete"
                getOptionLabel={(option) => {
                  if (String(option.Name) === "3") return "CASH";
                  if (String(option.Name) === "4") return "CASH PURCHASE";
                  return option.Name || "";
                }}
                options={headerInfo[1] || []}
                onChange={(e, newValue) =>
                  handleAutoCompleteChange(e, newValue, "dealer")
                }
                fullWidth
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Dealer" />
                )}
                value={returnHeader.dealer}
              />
            </Grid>
            <Grid item xs={6} sm={5} md={8}>
              <TextField
                label="Note"
                fullWidth
                size="small"
                value={returnHeader.note}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/* Dialog for Price Level Update */}
      <Dialog
        open={priceLevelPopup}
        onClose={cancelPriceLevelUpdate}
        aria-labelledby="price-level-dialog-title"
        aria-describedby="price-level-dialog-description"
      >
        <DialogTitle id="price-level-dialog-title">
          {"Update Prices?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="price-level-dialog-description">
            Do you want to update the selected items with the new Price Level's
            rate?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPriceLevelUpdate} color="primary">
            No
          </Button>
          <Button onClick={confirmPriceLevelUpdate} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SalesHeader;
